import { Alert } from "@itandi/itandi-bb-ui/Alert";
import { Button } from "@itandi/itandi-bb-ui/Button";
import cx from "clsx";
import { useRouter } from "next/router";
import { useCallback, useState } from "react";
import type { SortEnd, SortEndHandler } from "react-sortable-hoc";
import { SortableContainer, SortableElement } from "react-sortable-hoc";
import { SurveyModal } from "~/components/common/survey/SurveyModal";
import { SurveyListRow } from "~/components/specifics/vendors/settings/surveys/index/SurveyListRow";
import { useOpenHandler } from "~/hooks/useOpenHandler";
import type { NskResponse } from "~/models/Api";
import type { Survey } from "~/models/Survey";
import { MAX_NUMBER_OF_ACTIVE_SURVEYS } from "~/models/Survey";
import { PATH } from "~/pages/vendors/settings/surveys/new";
import Styles from "./SurveyList.module.scss";

type Props = {
	handleActivate: (surveyId: number) => Promise<NskResponse>;
	handleChangeEnabled: (
		surveyId: number,
		value: boolean
	) => Promise<NskResponse>;
	handleDestroy: (surveyId: number) => Promise<NskResponse>;
	handleUpdateSeq: (newOrderSurveys: Survey[]) => Promise<void>;
	surveys: Survey[];
};

type SurveyRowProps = Pick<
	Props,
	"handleActivate" | "handleChangeEnabled" | "handleDestroy"
> & {
	canActiveSurvey: boolean;
};

type SortableItemProps = SurveyRowProps & {
	survey: Survey;
};

const SortableItem = SortableElement<SortableItemProps>(
	({
		survey,
		canActiveSurvey,
		handleActivate,
		handleChangeEnabled,
		handleDestroy
	}: SortableItemProps) => (
		<SurveyListRow
			canActiveSurvey={canActiveSurvey}
			handleActivate={handleActivate}
			handleChangeEnabled={handleChangeEnabled}
			handleDestroy={handleDestroy}
			survey={survey}
		/>
	)
);

type SortableContainerProps = SurveyRowProps & {
	items: Survey[];
};

const SortableList = SortableContainer<SortableContainerProps>(
	({
		items,
		canActiveSurvey,
		handleActivate,
		handleChangeEnabled,
		handleDestroy
	}: SortableContainerProps) => (
		<tbody>
			{items.map((survey: Survey, index: number) => (
				<SortableItem
					canActiveSurvey={canActiveSurvey}
					handleActivate={handleActivate}
					handleChangeEnabled={handleChangeEnabled}
					handleDestroy={handleDestroy}
					index={index}
					key={survey.id}
					survey={survey}
				/>
			))}
		</tbody>
	)
);

export function SurveyList({
	surveys,
	handleActivate,
	handleChangeEnabled,
	handleUpdateSeq,
	handleDestroy
}: Props) {
	// 並び替え
	const [prevSurveys, setPrevSurveys] = useState(surveys);
	const [orderSurveys, setOrderSurveys] = useState(surveys);
	if (prevSurveys !== surveys) {
		setPrevSurveys(surveys);
		setOrderSurveys(surveys);
	}
	const {
		isOpen: openSurveyConfirm,
		handleOpen: handleOpenSurveyModal,
		handleClose: handleCloseSurveyModal
	} = useOpenHandler();
	const previewSurveys: Survey[] = orderSurveys.filter(
		(survey) => survey.enabled && survey.activatedAt !== null
	);
	const canActiveSurvey = previewSurveys.length < MAX_NUMBER_OF_ACTIVE_SURVEYS;
	const router = useRouter();
	const onClickNew = useCallback(() => {
		router.push(PATH).then();
	}, [router]);
	const onSortEnd: SortEndHandler = ({ oldIndex, newIndex }: SortEnd) => {
		if (oldIndex === newIndex) {
			return;
		}
		const moveSurvey = orderSurveys[oldIndex];
		const newOrderSurveys: Survey[] = [];
		const wkOrderSurveys = [...orderSurveys];
		wkOrderSurveys.splice(oldIndex, 1);
		wkOrderSurveys.splice(newIndex, 0, moveSurvey);
		wkOrderSurveys.forEach((r: Survey, i: number) => {
			newOrderSurveys.push({ ...r, seq: i + 1 });
		});
		setOrderSurveys(newOrderSurveys);
		handleUpdateSeq(newOrderSurveys).then();
	};
	return (
		<div>
			{!canActiveSurvey && (
				<Alert variant="info">
					有効なアンケートが上限の{MAX_NUMBER_OF_ACTIVE_SURVEYS}
					件に到達しています。
					<br />
					別のアンケートを有効にしたい場合は、有効なアンケートを一時停止してください。
				</Alert>
			)}
			<table className={Styles.List}>
				<thead>
					<tr className={cx(Styles.ListRow, Styles.Header)}>
						<th> </th>
						<th>状態</th>
						<th>設問</th>
						<th>選択肢</th>
						<th>備考欄</th>
						<th>操作</th>
						<th>有効化日</th>
						<th>削除</th>
					</tr>
				</thead>
				<SortableList
					canActiveSurvey={canActiveSurvey}
					handleActivate={handleActivate}
					handleChangeEnabled={handleChangeEnabled}
					handleDestroy={handleDestroy}
					items={orderSurveys}
					onSortEnd={onSortEnd}
					useDragHandle
					useWindowAsScrollContainer
				/>
			</table>
			<div className={Styles.Buttons}>
				<Button height="default" onClick={onClickNew} variant="secondary">
					アンケートを追加する
				</Button>
				{previewSurveys.length > 0 && (
					<Button
						className={Styles.RightButtons}
						height="default"
						onClick={handleOpenSurveyModal}
						variant="primary"
					>
						プレビュー
					</Button>
				)}
				<SurveyModal
					handleAccept={null}
					handleClose={handleCloseSurveyModal}
					isOpen={openSurveyConfirm}
					surveys={previewSurveys}
				/>
			</div>
		</div>
	);
}
